import { Receipt, Wallet, Landmark } from "lucide-react";
import { SectionCard, KpiCard, Segmented, InfoNote, Row } from "@/components/common/Cards";
import { Field, MoneyInput, NumInput } from "@/components/common/Inputs";
import { Sel, SwitchRow } from "@/components/common/Sel";
import { PPH23_TYPES } from "@/lib/engines/tax";
import { fmtRp, fmtPct } from "@/lib/format";

export const PPN_MODES = [
  { value: "dpp_nilai_lain", label: "PPN 12% × DPP nilai lain 11/12 (efektif 11%)" },
  { value: "full12", label: "PPN 12% penuh (barang mewah)" },
  { value: "custom", label: "Tarif PPN kustom" },
];

export function PpnControls({ ppn, onChange, ro, testid }) {
  return (
    <div className="space-y-3">
      <SwitchRow label="Kenakan PPN" hint="Penjual PKP menambahkan PPN pada faktur." checked={ppn.enabled} onChange={(v) => onChange({ ...ppn, enabled: v })} testid={`${testid}-ppn-switch`} disabled={ro} />
      {ppn.enabled && (
        <div className="grid grid-cols-[1fr_110px] gap-3">
          <Field label="Skema PPN">
            <Sel value={ppn.mode} onChange={(v) => onChange({ ...ppn, mode: v })} options={PPN_MODES} testid={`${testid}-ppn-mode`} disabled={ro} />
          </Field>
          <Field label="Tarif">
            <NumInput value={ppn.mode === "custom" ? ppn.customRate : ppn.mode === "full12" ? 12 : 11} onChange={(v) => onChange({ ...ppn, customRate: v })} suffix="%" disabled={ro || ppn.mode !== "custom"} testid={`${testid}-ppn-rate`} />
          </Field>
        </div>
      )}
    </div>
  );
}

export default function Pph23Panel({ data, setData, result: r, readOnly: ro }) {
  const f = (k) => (v) => setData((d) => ({ ...d, [k]: v }));
  const label = { dpp: "Nilai jasa / sewa (DPP, belum PPN)", incl_ppn: "Total tagihan termasuk PPN", net: "Nilai bersih yang ingin diterima vendor (di luar PPN)" }[data.basis];
  return (
    <div className="grid xl:grid-cols-[420px_1fr] gap-5">
      <SectionCard title="Input Transaksi" eyebrow="PPh Pasal 23" testid="pph23-input-card">
        <div className="space-y-4">
          <Field label="Jenis penghasilan">
            <Sel value={data.type} onChange={f("type")} options={PPH23_TYPES.map((t) => ({ value: t.key, label: `${t.label} — ${t.rate}%` }))} testid="pph23-type-select" disabled={ro} />
          </Field>
          <Field label="Dasar nilai">
            <Segmented
              value={data.basis}
              onChange={f("basis")}
              testid="pph23-basis"
              size="sm"
              disabled={ro}
              options={[
                { value: "dpp", label: "Gross (DPP)" },
                { value: "incl_ppn", label: "Termasuk PPN" },
                { value: "net", label: "Net (gross-up)" },
              ]}
            />
          </Field>
          <Field label={label}>
            <MoneyInput value={data.amount} onChange={f("amount")} testid="pph23-amount-input" disabled={ro} />
          </Field>
          <SwitchRow label="Penerima memiliki NPWP" hint="Tanpa NPWP tarif PPh 23 menjadi 2× (100% lebih tinggi)." checked={data.npwp} onChange={f("npwp")} testid="pph23-npwp-switch" disabled={ro} />
          <PpnControls ppn={data.ppn} onChange={f("ppn")} ro={ro} testid="pph23" />
        </div>
      </SectionCard>
      <div className="space-y-5">
        <div className="grid sm:grid-cols-3 gap-3">
          <KpiCard label="PPh 23 dipotong" value={fmtRp(r.pph)} sub={`Tarif ${fmtPct(r.rate)} × DPP`} accent icon={Landmark} testid="pph23-kpi-tax" />
          <KpiCard label="Total faktur" value={fmtRp(r.invoice)} sub={`DPP + PPN ${fmtPct(r.ppnRate)}`} icon={Receipt} testid="pph23-kpi-invoice" />
          <KpiCard label="Dibayar ke vendor" value={fmtRp(r.paidToVendor)} sub="Faktur − PPh 23 dipotong" icon={Wallet} testid="pph23-kpi-paid" />
        </div>
        <SectionCard title="Rincian invoice" testid="pph23-breakdown">
          <Row label="DPP (nilai jasa / sewa)" value={fmtRp(r.dpp)} />
          {r.dppNilaiLain !== null && <Row label="DPP nilai lain (11/12 × DPP)" value={fmtRp(r.dppNilaiLain)} muted />}
          <Row label={`PPN ${fmtPct(r.ppnRate)}`} value={fmtRp(r.ppn)} />
          <Row label="Total faktur" value={fmtRp(r.invoice)} strong />
          <Row label={`PPh 23 ${fmtPct(r.rate)} (dipotong pemberi penghasilan)`} value={fmtRp(-r.pph)} negative />
          <Row label="Ditransfer ke vendor" value={fmtRp(r.paidToVendor)} strong testid="pph23-paid-row" />
          <Row label="Pendapatan bersih vendor (di luar PPN)" value={fmtRp(r.vendorNetExPpn)} muted />
          <InfoNote className="mt-4">
            Pemberi penghasilan menyetor PPh 23 paling lambat tanggal 15 bulan berikutnya dan menerbitkan bukti potong (e-Bupot Unifikasi). PPN disetor oleh vendor PKP. PPh 23 bukan pajak final — menjadi kredit pajak bagi vendor.
          </InfoNote>
        </SectionCard>
      </div>
    </div>
  );
}
